import { gql, useLazyQuery } from '@apollo/client';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { Server } from './apollo/types';
import { logger } from './datadog';
import { isPayorExcludedFromSoftBlockAtom } from './visitor/state';

export type ClientExcludedFromSoftBlockByPayorQuery = {
  clientExcludedFromSoftBlockByPayor: boolean | null;
};

export type ClientExcludedFromSoftBlockByPayorQueryVariables = {
  patientShortId: string;
};

export const GET_PAYOR_SOFT_BLOCK_EXCLUSION = gql`
  query ClientExcludedFromSoftBlockByPayor($patientShortId: String!) {
    clientExcludedFromSoftBlockByPayor(patientShortId: $patientShortId)
  }
`;

const PAYOR_EXCLUSION_RESOLUTION_TIMEOUT_MS = 3_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('payor soft-block exclusion resolution timed out')), ms),
    ),
  ]);
}

/**
 * Returns a callback that resolves whether the client is excluded from the
 * telehealth soft block based on their payor (today: KPNC / Kaiser Permanente
 * Nor Cal) and stores it in `isPayorExcludedFromSoftBlockAtom`.
 *
 * Run during the pre-render gather phase, before the soft-block routing decision,
 * so the decision reads a settled value instead of racing an in-flight query.
 * Fails closed to `true` (excluded) on timeout/error/missing data so an excluded
 * payor is never soft-blocked, while still sending the client straight to the
 * waiting room (care is never blocked).
 */
export function useResolvePayorSoftBlockExclusionCallback() {
  const { patientShortId } = useParams();
  const setIsPayorExcluded = useSetAtom(isPayorExcludedFromSoftBlockAtom);
  const [fetchPayorExclusion] = useLazyQuery<
    ClientExcludedFromSoftBlockByPayorQuery,
    ClientExcludedFromSoftBlockByPayorQueryVariables
  >(GET_PAYOR_SOFT_BLOCK_EXCLUSION, {
    context: { server: Server.MONOLITH, skipAuth: true },
  });

  return useCallback(async () => {
    try {
      if (!patientShortId) {
        setIsPayorExcluded(true);
        return;
      }
      const result = await withTimeout(
        fetchPayorExclusion({ variables: { patientShortId } }),
        PAYOR_EXCLUSION_RESOLUTION_TIMEOUT_MS,
      );
      if (result.error) throw result.error;
      setIsPayorExcluded(result.data?.clientExcludedFromSoftBlockByPayor ?? true);
    } catch (error) {
      logger.error('Error resolving payor soft-block exclusion', { patientShortId, error });
      setIsPayorExcluded(true);
    }
  }, [fetchPayorExclusion, patientShortId, setIsPayorExcluded]);
}
